import { useCallback, useMemo, useRef, useState } from "react";
import {
	resolveExtendedSelection,
	resolveTreeKeyAction,
	type NavigableTreeRow,
} from "@/lib/file-tree-navigation";
import { reportSharedVolumeFailure } from "@/lib/shared-volume-failure";

/** 共有ボリュームのツリーの、選べる行。 */
export interface SharedVolumeNavigableRow {
	key: string;
	depth: number;
	/** 開ける (ディレクトリか、使える状態のボリュームの根)。 */
	expandable: boolean;
	expanded: boolean;
}

/**
 * 行の開閉。開くときに直下をまだ読んでいなければ読む (`loadChildren` は読んでいれば null を返す)。読めなければ畳み、
 * 開き直せば読み直す。
 */
export function useSharedVolumeTreeExpansion<Row extends SharedVolumeNavigableRow>(
	loadChildren: (row: Row) => Promise<void> | null,
) {
	const [expanded, setExpanded] = useState<ReadonlySet<string>>(() => new Set());

	const toggleExpanded = useCallback(
		(row: Row) => {
			const collapse = () =>
				setExpanded((current) => {
					const next = new Set(current);
					next.delete(row.key);
					return next;
				});
			if (row.expanded) {
				collapse();
				return;
			}
			setExpanded((current) => new Set(current).add(row.key));
			const loading = loadChildren(row);
			if (loading === null) return;
			reportSharedVolumeFailure(
				loading.catch((err: unknown) => {
					collapse();
					throw err;
				}),
				"共有ボリュームの中身を取得できませんでした。",
			);
		},
		[loadChildren],
	);

	return { expanded, toggleExpanded };
}

/**
 * 行の選択とキー操作。矢印キー・名前の変更 (Enter)・削除 (Cmd/Ctrl+Delete) は環境のファイルツリーと揃える。名前を変えられない
 * 行の Enter は行のクリック (開閉) にする。
 */
export function useSharedVolumeTreeSelection<Row extends SharedVolumeNavigableRow>({
	entryRows,
	toggleExpanded,
	menuOpen,
	renamable,
	onRename,
	onDelete,
	onSelect,
}: {
	entryRows: readonly Row[];
	toggleExpanded: (row: Row) => void;
	/** 右クリックのメニューを開いている間は、キー操作を受けない。 */
	menuOpen: boolean;
	renamable?: (row: Row) => boolean;
	onRename?: (row: Row) => void;
	/** 選んでいる行 (無ければキーを押した行) を消す。 */
	onDelete?: (rows: Row[]) => void;
	/** クリック・右クリック・キー操作で行を選んだとき (選んでいる行が変わらなくても呼ぶ)。 */
	onSelect?: () => void;
}) {
	const [selected, setSelected] = useState<ReadonlySet<string>>(() => new Set());
	const anchorRef = useRef<string | null>(null);
	const treeRef = useRef<HTMLDivElement>(null);

	const navigableRows = useMemo(
		() =>
			entryRows.map(
				(row): NavigableTreeRow => ({
					entry: { type: row.expandable ? "directory" : "file", path: row.key, children: null },
					depth: row.depth,
					expanded: row.expanded,
				}),
			),
		[entryRows],
	);

	const selectRow = useCallback(
		(row: Row, event: React.MouseEvent) => {
			onSelect?.();
			if (event.shiftKey) {
				const { paths, anchorPath } = resolveExtendedSelection(
					navigableRows,
					anchorRef.current,
					row.key,
					row.key,
				);
				if (paths.length > 0) {
					setSelected(new Set(paths));
					anchorRef.current = anchorPath;
					return;
				}
			}
			if (event.metaKey || event.ctrlKey) {
				setSelected((current) => {
					const next = new Set(current);
					if (next.has(row.key)) next.delete(row.key);
					else next.add(row.key);
					return next;
				});
				anchorRef.current ??= row.key;
				return;
			}
			setSelected(new Set([row.key]));
			anchorRef.current = row.key;
			if (row.expandable) toggleExpanded(row);
		},
		[navigableRows, onSelect, toggleExpanded],
	);

	/** 右クリックした行を含む選択 (選んでいない行なら、その行だけを選び直す)。 */
	const selectForMenu = useCallback(
		(row: Row): ReadonlySet<string> => {
			onSelect?.();
			if (selected.has(row.key)) return selected;
			const keys = new Set([row.key]);
			setSelected(keys);
			anchorRef.current = row.key;
			return keys;
		},
		[onSelect, selected],
	);

	const focusRow = useCallback((key: string) => {
		const rowsInTree = treeRef.current?.querySelectorAll<HTMLElement>("[data-row-key]") ?? [];
		[...rowsInTree].find((el) => el.dataset.rowKey === key)?.focus();
	}, []);

	const handleRowKeyDown = useCallback(
		(row: Row, event: React.KeyboardEvent<HTMLButtonElement>) => {
			event.stopPropagation();
			if (event.nativeEvent.isComposing || menuOpen) return;
			const action = resolveTreeKeyAction({
				key: event.key,
				shiftKey: event.shiftKey,
				metaKey: event.metaKey,
				ctrlKey: event.ctrlKey,
				altKey: event.altKey,
				rows: navigableRows,
				currentPath: row.key,
				selectedCount: selected.size,
			});
			switch (action?.type) {
				case "move": {
					event.preventDefault();
					onSelect?.();
					const { paths, anchorPath } = action.extend
						? resolveExtendedSelection(navigableRows, anchorRef.current, row.key, action.path)
						: { paths: [action.path], anchorPath: action.path };
					setSelected(new Set(paths.length > 0 ? paths : [action.path]));
					anchorRef.current = paths.length > 0 ? anchorPath : action.path;
					focusRow(action.path);
					break;
				}
				case "setExpanded": {
					event.preventDefault();
					const target = entryRows.find((candidate) => candidate.key === action.path);
					if (target) toggleExpanded(target);
					break;
				}
				case "selectAll":
					event.preventDefault();
					onSelect?.();
					setSelected(new Set(entryRows.map((candidate) => candidate.key)));
					anchorRef.current = row.key;
					break;
				case "clearSelection":
					event.preventDefault();
					setSelected(new Set());
					anchorRef.current = null;
					break;
				case "rename":
					if (onRename === undefined || renamable?.(row) !== true) break;
					event.preventDefault();
					onRename(row);
					break;
				case "delete": {
					if (onDelete === undefined) break;
					event.preventDefault();
					const rows = entryRows.filter((candidate) => selected.has(candidate.key));
					onDelete(rows.length > 0 ? rows : [row]);
					break;
				}
				case "suppress":
					event.preventDefault();
					break;
			}
		},
		[
			entryRows,
			focusRow,
			menuOpen,
			navigableRows,
			onDelete,
			onRename,
			onSelect,
			renamable,
			selected,
			toggleExpanded,
		],
	);

	const clearSelection = useCallback(() => {
		setSelected(new Set());
		anchorRef.current = null;
	}, []);

	const clearSelectionOnBlank = useCallback((event: React.MouseEvent) => {
		event.stopPropagation();
		if (event.target !== event.currentTarget) return;
		setSelected(new Set());
		anchorRef.current = null;
	}, []);

	// Tab で入る行は 1 つにする (選んでいる最初の行、無ければ先頭)。
	const currentKey =
		entryRows.find((row) => selected.has(row.key))?.key ?? entryRows[0]?.key ?? null;

	return {
		selected,
		treeRef,
		currentKey,
		selectRow,
		selectForMenu,
		handleRowKeyDown,
		clearSelection,
		clearSelectionOnBlank,
	};
}
